import { useCallback, useRef } from 'react'
import { postToolResponse } from '../api/tool-response'
import { useAuth } from '../providers'

export type AutoToolTracker = {
  /**
   * Post a "tool not found" result for a client tool the frontend can't run,
   * after a short delay, then kick a follow-up turn once the stream has
   * completed and every scheduled response has posted.
   */
  scheduleFailedToolResponse: (toolId: string, errorMessage: string) => void
  /** The submission's stream finished — start the follow-up if one is due. */
  onStreamCompleted: () => void
}

/**
 * Per-submission tracker for auto-posted failed-tool responses. The dedup set
 * spans submissions (hook-level) so a retried turn doesn't double-post for
 * the same tool call id.
 */
export function useCreateAutoToolTracker() {
  const { getAuthToken } = useAuth()
  const seenToolFailureIdsRef = useRef<Set<string>>(new Set())

  return useCallback(
    (params: { getConversationId: () => string; startFollowUp: () => Promise<void> }): AutoToolTracker => {
      let streamCompleted = false
      let responsesExpected = 0
      let responsesSettled = 0
      let responsesPosted = 0
      let followUpStarted = false

      const maybeStartFollowUp = () => {
        if (!streamCompleted || responsesExpected === 0 || responsesSettled < responsesExpected || followUpStarted) {
          return
        }
        // Nothing actually landed — a continuation turn would resume the
        // model with its tool call still unanswered.
        if (responsesPosted === 0) return
        followUpStarted = true
        void params.startFollowUp().catch((error) => {
          console.error('Failed to continue chat after tool response:', error)
        })
      }

      return {
        scheduleFailedToolResponse: (toolId, errorMessage) => {
          if (seenToolFailureIdsRef.current.has(toolId)) return
          seenToolFailureIdsRef.current.add(toolId)
          responsesExpected += 1

          window.setTimeout(() => {
            void (async () => {
              try {
                const accessToken = await getAuthToken()
                const response = await postToolResponse({
                  accessToken,
                  conversationId: params.getConversationId(),
                  toolCallId: toolId,
                  toolResponse: JSON.stringify([{ type: 'text', text: errorMessage }]),
                })
                if (response.ok) {
                  responsesPosted += 1
                } else {
                  // Un-latch the dedup so a retried turn can post again.
                  seenToolFailureIdsRef.current.delete(toolId)
                  console.error(`Failed to submit fallback tool response: HTTP ${response.status}`)
                }
              } catch (error) {
                seenToolFailureIdsRef.current.delete(toolId)
                console.error('Failed to submit fallback tool response:', error)
              } finally {
                responsesSettled += 1
                maybeStartFollowUp()
              }
            })()
          }, 2000)
        },
        onStreamCompleted: () => {
          streamCompleted = true
          maybeStartFollowUp()
        },
      }
    },
    [getAuthToken],
  )
}
